import React, { useContext } from "react";
import { Link } from "gatsby";
import { Paper, Container, Box, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { SiteContext, SingleContext } from "gatsby-frontend";
import { Breadcrumbs, BookmarkToggle, Image, Share } from "./../../";

function TitleBar({
	title,
	title_append,
	subtitle,
	align,
	image,
	layout,
	shareButtons,
	headingType = "h1",
}) {
	const theme = useTheme();
	const { isPrivateLayout, bookmarkableBundles } = useContext(SiteContext);
	const { ancestors, bundle } = useContext(SingleContext);
	const titleBarColors = isPrivateLayout
		? theme.palette.titleBar.private
		: theme.palette.titleBar.public;
	const titleFont = theme.typography.titleBar
		? theme.typography.titleBar
		: null;
	const showBookmarkToggle =
		layout !== "blog" &&
		process.env.GATSBY_IS_BOOKMARK_PAGES_ENABLED &&
		bookmarkableBundles.includes(bundle);

	const parent = layout === "blog" ? ancestors[0] : null;
	const main_title = title;
	if (parent) {
		title = parent.title;
	}

	const hasImage = layout === "right" && layout !== "blog" && image;
	const hasBackground = layout === "background" && layout !== "blog" && image;

	return (
		<Paper
			className={`title-bar ${layout ? `layout-${layout}` : ""} ${
				hasImage ? "has-image" : "no-image"
			} ${hasBackground ? "has-background" : "no-background"}`}
			sx={{
				backgroundColor: titleBarColors.dark,
				borderRadius: 0,
				...(titleBarColors.gradient && {
					backgroundImage: titleBarColors.gradient,
				}),
				...(hasBackground && {
					backgroundImage: `url(${image.src})`,
				}),
				"&.layout-background.has-background": {
					backgroundSize: "cover",
					backgroundPosition: "center",
					backgroundRepeat: "no-repeat",
					marginBottom: theme.spacing(4),
					color: "#fff",
					textShadow: "1px 1px 2px rgba(0,0,0,0.25)",
					padding: theme.spacing(13, 0, 10),
					[theme.breakpoints.down("xl")]: {
						padding: theme.spacing(9, 0, 6),
					},
					[theme.breakpoints.down("sm")]: {
						padding: theme.spacing(6, 0, 4),
					},
					position: "relative",
					"& .MuiTypography-h1, & a, & .MuiTypography-body1, & .MuiTypography-subtitle1":
						{
							color: "#fff",
						},
					"&:before": {
						content: "''",
						position: "absolute",
						top: 0,
						left: 0,
						width: "100%",
						height: "100%",
						backgroundImage:
							"linear-gradient(to right, rgba(0,0,0,0.38), rgba(0,0,0,0.1))",
						zIndex: 1,
					},
					"& > .MuiContainer-root ": {
						position: "relative",
						zIndex: 2,
					},
				},
				"&.layout-left, &.layout-right": {
					"&.has-image": {
						"& .MuiContainer-root": {
							padding: "0 !important",
							display: "flex",
							flexWrap: "wrap",
							alignItems: "center",
							"& header.MuiBox-root": {
								width: "50%",
								textAlign: "left",
								[theme.breakpoints.down("md")]: {
									width: "100%",
									padding: theme.spacing(4, 2),
								},
								"& > *": {
									display: "block",
									marginX: "auto",
									width: "50%",
									[theme.breakpoints.down("md")]: {
										width: "100%",
									},
								},
							},
							"& figure.image": {
								margin: 0,
								width: "50%",
								" .MuiPaper-root": {
									borderRadius: 0,
								},
								[theme.breakpoints.down("md")]: {
									width: "100%",
								},
								"& figcaption": {
									display: "none",
								},
							},
						},
					},
					"&.no-image": {},
				},
			}}
		>
			<Container
				maxWidth={theme?.customOptions?.containerMaxWidth || "xl"}
				sx={{ padding: theme.spacing(3, 0) }}
			>
				{
					<>
						{layout === "left" && layout !== "blog" && image && (
							<Image {...image} href={null} />
						)}
						<Box
							component="header"
							sx={{
								color: titleBarColors.contrastText,
								"& .MuiTypography-body1, & a": {
									color: titleBarColors.contrastText,
								},
							}}
						>
							{layout !== "blog" && ancestors && ancestors.length > 0 && (
								<Box sx={{ marginTop: theme.spacing(1) }}>
									<Breadcrumbs ancestors={ancestors} title={main_title} />
								</Box>
							)}
							<Typography
								variant={layout === "blog" ? "h4" : headingType}
								component={layout === "blog" ? Link : headingType}
								sx={{
									...(align === "center" && {
										textAlign: "center",
									}),
									...(titleFont && { ...titleFont }),
								}}
								{...(layout === "blog" && { to: parent.slug })}
							>
								{title}
								{title_append && (
									<>
										{", "}
										<small>{title_append}</small>
									</>
								)}
								{showBookmarkToggle && (
									<BookmarkToggle color={titleBarColors.contrastText} />
								)}
							</Typography>
							{layout !== "blog" && subtitle && (
								<Typography
									variant="subtitle1"
									component="em"
									sx={{ marginTop: theme.spacing(2) }}
								>
									{subtitle}
								</Typography>
							)}
							{shareButtons && shareButtons.length > 0 && (
								<Share
									buttons={shareButtons}
									color={
										hasBackground ? "#ffffff" : titleBarColors.contrastText
									}
								/>
							)}
						</Box>
						{hasImage && <Image {...image} href={null} />}
					</>
				}
			</Container>
		</Paper>
	);
}

export default TitleBar;
